import { IPaginated, IPaginationSearch } from '@shared/common';

import { CompanyType } from '../../../companyService/company/types/enums';
import { InspectionFlowType } from '../../inspectionFlows/types/enums';
import { StatusInspection } from '../../inspections/types/enums';

import { OperationsDateKind, OperationsInspectorSortField, UsageStage, UsageTrendBucket } from './enums';
import {
  IAnalyticsMetricAnnotation,
  IUsageAnalyticsFilter,
  IUsageDurationStats,
  IUsagePeriod,
} from './usage-analytics';

/**
 * Shared usage filters extended with operations-specific inspection filters.
 * Operations analytics are inspection-level and support all company types, including rental.
 * When verticals are omitted, all company types are included.
 *
 * @interface IOperationsAnalyticsFilter
 * @extends IUsageAnalyticsFilter
 * @property {number[]} [inspectorIds] - Estimation inspector IDs from `inspections.inspector_id`.
 * @property {InspectionFlowType[]} [flowTypes] - Inspection flow types to include.
 */
export interface IOperationsAnalyticsFilter extends IUsageAnalyticsFilter {
  inspectorIds?: number[];
  flowTypes?: InspectionFlowType[];
}

/**
 * Duration statistics extended with the 90th percentile.
 * Inherits the sample count, average duration, and median duration.
 *
 * @interface IOperationsDurationStats
 * @extends IUsageDurationStats
 * @property {number | null} p90Minutes - 90th-percentile duration in minutes, or null when no valid samples exist.
 */
export interface IOperationsDurationStats extends IUsageDurationStats {
  p90Minutes: number | null;
}

/**
 * Inspection counts calculated from inspection base rows.
 * Created inspections form a cohort whose current stages determine open and closed counts.
 * Completed inspections are counted by completion date and may have been created before the period.
 *
 * @interface IOperationsInspectionCounts
 * @property {number} created - Inspections created in the selected period.
 * @property {number} completed - Inspections whose service completion occurred in the selected period.
 * @property {number | null} completionRate - `completed / created`, or null when created is zero; may exceed 1.
 * @property {number} inProgress - Inspections from the created cohort currently in awaiting, capture, or review.
 * @property {number} cancelled - Inspections from the created cohort currently in the closed stage.
 * @property {number} attention - Inspections from the created cohort currently marked REQUIRES_ATTENTION.
 */
export interface IOperationsInspectionCounts {
  created: number;
  completed: number;
  completionRate: number | null;
  inProgress: number;
  cancelled: number;
  attention: number;
}

/**
 * Quality metrics for inspections completed in the selected period.
 * Shares and rates are fractions, not percentages.
 *
 * @interface IOperationsQualityStats
 * @property {number} redoInspections - Completed inspections that went through at least one redo.
 * @property {number | null} redoRate - Fraction of completed inspections with a redo, or null when none completed.
 * @property {number} withDamages - Completed inspections with at least one active damage or final damages.
 * @property {number | null} withDamagesShare - Fraction of completed inspections with damages, or null when none completed.
 * @property {number | null} avgDamagesPerInspection - Active damage count divided by completed inspections, or null when none completed.
 * @property {number} crInspections - Completed inspections with a condition-report score.
 * @property {number | null} crShare - Fraction of completed inspections with a condition-report score, or null when none completed.
 * @property {number} estimateInspections - Completed inspections with a monetary estimate.
 * @property {number | null} estimateShare - Fraction of completed inspections with a monetary estimate, or null when none completed.
 * @property {number | null} stepsDoneRate - Average valid `steps_done / steps_count` ratio, or null when unavailable.
 */
export interface IOperationsQualityStats {
  redoInspections: number;
  redoRate: number | null;
  withDamages: number;
  withDamagesShare: number | null;
  avgDamagesPerInspection: number | null;
  crInspections: number;
  crShare: number | null;
  estimateInspections: number;
  estimateShare: number | null;
  stepsDoneRate: number | null;
}

/**
 * Operations metrics grouped by inspection flow type.
 *
 * @interface IOperationsFlowTypeItem
 * @property {InspectionFlowType} flowType - Inspection flow type represented by the group.
 * @property {number} created - Inspections of this flow type created in the selected period.
 * @property {number} completed - Inspections of this flow type completed in the selected period.
 * @property {number | null} completionRate - `completed / created`, or null when created is zero; may exceed 1.
 * @property {number | null} captureMedianMinutes - Median valid capture duration for period completions, or null when unavailable.
 */
export interface IOperationsFlowTypeItem {
  flowType: InspectionFlowType;
  created: number;
  completed: number;
  completionRate: number | null;
  captureMedianMinutes: number | null;
}

/**
 * Operations analytics summary for the resolved reporting period.
 *
 * @interface IOperationsSummaryResult
 * @property {IUsagePeriod} period - Resolved reporting dates and timezone.
 * @property {IOperationsInspectionCounts} inspections - Inspection volume and lifecycle counts.
 * @property {IOperationsDurationStats} capture - Capture duration statistics for inspections completed in the period.
 * @property {IOperationsDurationStats} turnaround - Turnaround duration statistics for inspections completed in the period.
 * @property {IOperationsQualityStats} quality - Quality metrics for inspections completed in the period.
 * @property {IOperationsFlowTypeItem[]} byFlowType - Metrics grouped by inspection flow type.
 */
export interface IOperationsSummaryResult {
  period: IUsagePeriod;
  inspections: IOperationsInspectionCounts;
  capture: IOperationsDurationStats;
  turnaround: IOperationsDurationStats;
  quality: IOperationsQualityStats;
  byFlowType: IOperationsFlowTypeItem[];
}

/**
 * Filters and time-bucket configuration for operations trends.
 *
 * @interface IOperationsTrendParams
 * @extends IOperationsAnalyticsFilter
 * @property {UsageTrendBucket} [bucket] - Calendar interval used to group trend metrics.
 */
export interface IOperationsTrendParams extends IOperationsAnalyticsFilter {
  bucket?: UsageTrendBucket;
}

/**
 * Operations metrics for one calendar bucket.
 * Created counts use creation dates; completion, duration, and quality metrics use completion dates.
 *
 * @interface IOperationsTrendPoint
 * @property {string} bucket - Bucket start date in YYYY-MM-DD format, calculated in the reporting timezone.
 * @property {number} created - Inspections created within the bucket and reporting period.
 * @property {number} completed - Inspections completed within the bucket and reporting period.
 * @property {number | null} captureMedianMinutes - Median valid capture duration for bucket completions, or null when unavailable.
 * @property {number | null} turnaroundMedianMinutes - Median valid turnaround duration for bucket completions, or null when unavailable.
 * @property {number} withDamages - Inspections completed in the bucket with active or final damages.
 * @property {number} redo - Inspections completed in the bucket that went through at least one redo.
 */
export interface IOperationsTrendPoint {
  bucket: string;
  created: number;
  completed: number;
  captureMedianMinutes: number | null;
  turnaroundMedianMinutes: number | null;
  withDamages: number;
  redo: number;
}

/**
 * Time-bucketed operations analytics response.
 *
 * @interface IOperationsTrendResult
 * @property {IUsagePeriod} period - Resolved reporting dates and timezone.
 * @property {UsageTrendBucket} bucket - Calendar interval used to group the results.
 * @property {IOperationsTrendPoint[]} items - Trend points ordered by bucket date.
 */
export interface IOperationsTrendResult {
  period: IUsagePeriod;
  bucket: UsageTrendBucket;
  items: IOperationsTrendPoint[];
}

/**
 * Paginated inspector analytics request with filtering and sorting.
 * Inspector name search is handled by the gateway because names reside in auth-service.
 *
 * @interface IPaginationSearchOperationsInspectors
 * @extends IPaginationSearch
 * @extends IOperationsAnalyticsFilter
 * @property {OperationsInspectorSortField} [sortBy] - Metric used to sort inspector rows; defaults to completed count.
 */
export interface IPaginationSearchOperationsInspectors extends IPaginationSearch, IOperationsAnalyticsFilter {
  sortBy?: OperationsInspectorSortField;
}

/**
 * Metrics for one estimation inspector within the cohort created in the selected period.
 * Completion is determined by the cohort's current stage, not its completion date.
 * Quality shares use completed cohort inspections as their denominator and are fractions.
 *
 * @interface IOperationsInspectorItem
 * @property {number | null} inspectorId - Estimation inspector ID, or null for inspections without an assigned inspector.
 * @property {number} total - Inspections attributed to this inspector and created in the selected period.
 * @property {number} completed - Inspections from this inspector's created cohort currently in the completed stage.
 * @property {number | null} completionRate - `completed / total`, or null when total is zero.
 * @property {number | null} captureMedianMinutes - Median valid capture duration for completed cohort inspections, or null when unavailable.
 * @property {number | null} turnaroundMedianMinutes - Median valid turnaround duration for completed cohort inspections, or null when unavailable.
 * @property {number | null} withDamagesShare - Fraction of completed cohort inspections with damages, or null when none completed.
 * @property {number | null} crShare - Fraction of completed cohort inspections with a condition-report score, or null when none completed.
 * @property {number | null} estimateShare - Fraction of completed cohort inspections with a monetary estimate, or null when none completed.
 * @property {number | null} redoRate - Fraction of completed cohort inspections with at least one redo, or null when none completed.
 * @property {string} [inspectorName] - Inspector display name supplied through enrichment.
 */
export interface IOperationsInspectorItem {
  inspectorId: number | null;
  total: number;
  completed: number;
  completionRate: number | null;
  captureMedianMinutes: number | null;
  turnaroundMedianMinutes: number | null;
  withDamagesShare: number | null;
  crShare: number | null;
  estimateShare: number | null;
  redoRate: number | null;
  inspectorName?: string;
}

/**
 * Paginated inspector analytics response with reporting-period metadata.
 * Inspector rows and pagination metadata are inherited from IPaginated.
 *
 * @interface IOperationsInspectorsResult
 * @extends {IPaginated<IOperationsInspectorItem[]>}
 * @property {IUsagePeriod} period - Resolved reporting dates and timezone.
 */
export interface IOperationsInspectorsResult extends IPaginated<IOperationsInspectorItem[]> {
  period: IUsagePeriod;
}

/**
 * Timezone-aware status-series request for the operations dashboard.
 * Date selection applies to series and totals; period-based quick stats always use completion dates.
 * Snapshot quick stats remain independent of the requested period and status filter.
 *
 * @interface IOperationsStatusSeriesParams
 * @extends IUsageAnalyticsFilter
 * @property {UsageTrendBucket} [bucket] - Calendar interval used to group status-series rows.
 * @property {OperationsDateKind} [dateKind] - Date field used to filter and bucket series and totals.
 * @property {StatusInspection[]} [statuses] - Statuses included in series, totals, and period-based quick stats.
 * @property {StatusSeriesInclude[]} [include] - Response sections to return; defaults to totals and mapping coverage.
 */
export interface IOperationsStatusSeriesParams extends IUsageAnalyticsFilter {
  bucket?: UsageTrendBucket;
  dateKind?: OperationsDateKind;
  statuses?: StatusInspection[];
  include?: StatusSeriesInclude[];
}

/**
 * Default inspection-age thresholds in whole days for backlog stages.
 * Age is measured from inspection creation, not from entry into the current stage.
 * Awaiting allows 2 days; capture and review allow 1, because the first is the client's own time.
 * Whole days rather than hours so the cut is one a `YYYY-MM-DD` filter can express exactly, letting
 * a consumer link the number through to a list and get the same rows back.
 * Completed and closed stages are not open backlog stages and have zero placeholders.
 * Each threshold is echoed in the corresponding backlog stage response.
 *
 * @constant {Readonly<Record<UsageStage, number>>} QUICK_STAT_BACKLOG_DEFAULT_AGED_DAYS
 */
export const QUICK_STAT_BACKLOG_DEFAULT_AGED_DAYS: Readonly<Record<UsageStage, number>> = {
  [UsageStage.AWAITING]: 2,
  [UsageStage.CAPTURE]: 1,
  [UsageStage.REVIEW]: 1,
  [UsageStage.COMPLETED]: 0,
  [UsageStage.CLOSED]: 0,
};

/**
 * Response sections available for an operations status-series request.
 *
 * @enum {string}
 */
export enum StatusSeriesInclude {
  /** Time-bucketed inspection counts grouped by company type and current status. */
  SERIES = 'series',
  /** Inspection totals grouped by company type and current status. */
  TOTALS = 'totals',
  /** Company-type mapping coverage metadata. */
  COVERAGE = 'coverage',
  /** Dashboard metrics, including period comparisons and live backlog snapshots. */
  QUICK_STATS = 'quickStats',
}

/**
 * Inspection count for one time bucket, company type, and current status.
 * Company and location identifiers may provide additional grouping for cross-tenant results.
 *
 * @interface IStatusSeriesItem
 * @property {string} bucket - Bucket start date in YYYY-MM-DD format, calculated in the reporting timezone.
 * @property {CompanyType | 'UNKNOWN'} companyType - Company type, or UNKNOWN when unavailable.
 * @property {StatusInspection} status - Current inspection status represented by the group.
 * @property {number} count - Number of matching inspections in the group.
 * @property {number} [companyId] - Company identifier when company-level grouping is included.
 * @property {number} [locationId] - Location identifier when location-level grouping is included.
 * @property {string} [companyName] - Company display name supplied through enrichment.
 * @property {string} [locationName] - Location display name supplied through enrichment.
 */
export interface IStatusSeriesItem {
  bucket: string;
  companyType: CompanyType | 'UNKNOWN';
  status: StatusInspection;
  count: number;
  companyId?: number;
  locationId?: number;
  companyName?: string;
  locationName?: string;
}

/**
 * Inspection total for one company type and current status across the selected period.
 * Company and location identifiers may provide additional grouping for cross-tenant results.
 *
 * @interface IStatusTotalsItem
 * @property {CompanyType | 'UNKNOWN'} companyType - Company type, or UNKNOWN when unavailable.
 * @property {StatusInspection} status - Current inspection status represented by the group.
 * @property {number} count - Number of matching inspections in the group.
 * @property {number} [companyId] - Company identifier when company-level grouping is included.
 * @property {number} [locationId] - Location identifier when location-level grouping is included.
 * @property {string} [companyName] - Company display name supplied through enrichment.
 * @property {string} [locationName] - Location display name supplied through enrichment.
 */
export interface IStatusTotalsItem {
  companyType: CompanyType | 'UNKNOWN';
  status: StatusInspection;
  count: number;
  companyId?: number;
  locationId?: number;
  companyName?: string;
  locationName?: string;
}

/**
 * Company-type mapping coverage metadata.
 * The current producer returns fixed values of exact = 1, byCompany = 0, and unknown = 0;
 * these values are not calculated inspection counts.
 *
 * @interface IMappingCoverage
 * @property {number} exact - Coverage value for exact mapping.
 * @property {number} byCompany - Coverage value for company-level fallback mapping.
 * @property {number} unknown - Coverage value for unresolved mapping.
 */
export interface IMappingCoverage {
  exact: number;
  byCompany: number;
  unknown: number;
}

/**
 * Metric comparison with the immediately preceding reporting period of equal calendar-day length.
 *
 * @interface IQuickStatProgress
 * @property {number | null} previous - Previous-period metric value in the same units, or null when unavailable.
 * @property {number | null} delta - Current value minus previous value, or null when either value is unavailable.
 * @property {number | null} percent - `(delta / previous) * 100`, or null when either value is unavailable or previous is zero.
 */
export interface IQuickStatProgress {
  previous: number | null;
  delta: number | null;
  percent: number | null;
}

/**
 * Duration-based dashboard metric with an optional previous-period comparison.
 *
 * @interface IQuickStatDurationMetric
 * @property {number | null} minutes - Duration in minutes, or null when no valid duration samples exist.
 * @property {IQuickStatProgress} [progress] - Comparison with the previous reporting period.
 */
export interface IQuickStatDurationMetric {
  minutes: number | null;
  progress?: IQuickStatProgress;
}

/**
 * Count-based dashboard metric with an optional previous-period comparison.
 *
 * @interface IQuickStatCountMetric
 * @property {number} value - Inspection count represented by the metric.
 * @property {IQuickStatProgress} [progress] - Comparison with the previous reporting period.
 */
export interface IQuickStatCountMetric {
  value: number;
  progress?: IQuickStatProgress;
}

/**
 * Current inspection count and age breakdown for one open backlog stage.
 * Age is measured from `created_at`, not from the time the inspection entered the stage.
 * Day boundaries are read in the request `timezone` (default UTC), so "two days old" means two of
 * the reader's own days — the same rule `thisMonth` already follows.
 *
 * @interface IQuickStatBacklogStage
 * @property {UsageStage} stage - Open lifecycle stage: awaiting, capture, or review.
 * @property {number} value - Inspections currently in this stage.
 * @property {number} aged - Subset of stage inspections created on or before `today - agedAfterDays`.
 * @property {number} agedAfterDays - Inspection-age threshold in whole days used to calculate aged.
 */
export interface IQuickStatBacklogStage {
  stage: UsageStage;
  value: number;
  aged: number;
  agedAfterDays: number;
}

/**
 * Live backlog snapshot for inspections within the resolved scope.
 * Independent of the requested reporting period and status filter.
 * Open stages follow USAGE_IN_PROGRESS_STAGES and USAGE_STAGE_STATUSES.
 * Finished inspections requiring attention are reported separately from open stages.
 *
 * @interface IQuickStatBacklog
 * @property {IQuickStatBacklogStage[]} stages - Current counts and age breakdowns for awaiting, capture, and review.
 * @property {number} attention - Finished inspections marked REQUIRES_ATTENTION; excluded from open-stage counts.
 * @property {string} asOf - ISO 8601 timestamp identifying when the snapshot response was assembled.
 */
export interface IQuickStatBacklog {
  stages: IQuickStatBacklogStage[];
  attention: number;
  asOf: string;
}

/**
 * Quick dashboard metrics combining period-based results with current scoped snapshots.
 * Completed count and average capture duration include previous-period comparisons.
 * Total, current-month, pending, and backlog metrics are independent of the selected period
 * and status filter.
 *
 * @interface IQuickStats
 * @property {IQuickStatCountMetric} [completed] - Inspections completed in the selected period, with previous-period progress.
 * @property {IQuickStatDurationMetric} [avgTime] - Average valid capture duration for period completions, with previous-period progress.
 * @property {IQuickStatCountMetric} [total] - Total inspections currently in scope, regardless of reporting period.
 * @property {IQuickStatCountMetric} [thisMonth] - Scoped inspections created in the current calendar month of the reporting timezone.
 * @property {IQuickStatCountMetric} [pending] - Sum of awaiting, capture, and review counts in backlog.stages.
 * @property {IQuickStatBacklog} [backlog] - Live open-stage counts, age breakdowns, attention count, and snapshot timestamp.
 */
export interface IQuickStats {
  completed?: IQuickStatCountMetric;
  avgTime?: IQuickStatDurationMetric;
  total?: IQuickStatCountMetric;
  thisMonth?: IQuickStatCountMetric;
  pending?: IQuickStatCountMetric;
  backlog?: IQuickStatBacklog;
}

/**
 * Operations status-series response containing the requested sections.
 * Optional sections are populated according to the request's include selection.
 *
 * @interface IOperationsStatusSeriesResult
 * @property {IUsagePeriod} period - Resolved reporting dates and timezone.
 * @property {IStatusSeriesItem[]} [series] - Time-bucketed inspection counts grouped by company type and current status.
 * @property {IStatusTotalsItem[]} [totalsByCompanyTypeStatus] - Period totals grouped by company type and current status.
 * @property {IMappingCoverage} [mappingCoverage] - Company-type mapping coverage metadata.
 * @property {IQuickStats} [quickStats] - Period-based dashboard metrics and current scoped snapshots.
 */
export interface IOperationsStatusSeriesResult {
  period: IUsagePeriod;
  series?: IStatusSeriesItem[];
  totalsByCompanyTypeStatus?: IStatusTotalsItem[];
  mappingCoverage?: IMappingCoverage;
  quickStats?: IQuickStats;
}

/**
 * The operations summary tiles, keyed by the field on {@link IOperationsSummaryResult} each one
 * reads. Every figure is inspection-level: one inspection, counted once, whatever it was asked for.
 *
 * The timing tiles report the median, as the usage page does, so the two reconcile for one filter.
 * The average is kept beside the number rather than as the headline: one abandoned session moves an
 * average by hours and leaves a median where it was.
 */
export type OperationsSummaryMetric =
  | 'inspections.created'
  | 'inspections.completed'
  | 'inspections.inProgress'
  | 'inspections.attention'
  | 'capture'
  | 'turnaround'
  | 'quality.redoRate'
  | 'quality.withDamagesShare'
  | 'quality.crShare'
  | 'quality.estimateShare';

export const OPERATIONS_SUMMARY_METRICS: Readonly<Record<OperationsSummaryMetric, IAnalyticsMetricAnnotation>> = {
  'inspections.completed': {
    label: 'Inspections completed',
    description:
      'Inspections whose service completed inside the selected period, whenever they were created. The share underneath is against what the period created, which is a different set of inspections.',
  },
  'inspections.created': {
    label: 'Inspections created',
    description:
      'Inspections opened inside the period. The breakdown underneath follows that same cohort to wherever it stands now, so it keeps moving after the period has closed.',
  },
  'inspections.inProgress': {
    label: 'Inspections in progress',
    description:
      'Of the inspections created in this period, those still in an open phase right now: awaiting the client, in capture, or in review.',
  },
  'inspections.attention': {
    label: 'Requiring attention',
    description:
      'Inspections created in this period that now sit at Requires attention: the report came back, but something on it needs a person.',
  },
  capture: {
    label: 'Capture time',
    description:
      'From the client opening the inspection to submitting it, over the inspections that completed inside the period. The median, so one session left open overnight cannot move it. Where no open timestamp was recorded, creation stands in for it, and an inspection that was never submitted is left out.',
    formula: 'Submitted − Opened, median',
    example: 'Opened 09:12 → Submitted 09:30 = 18m',
  },
  turnaround: {
    label: 'Turnaround',
    description:
      'Taken over the inspections that completed inside the period. It spans every wait in between — the client, the reviewer and any redo — so it is always longer than capture time.',
    formula: 'Completed − Created, median',
    example: 'Created Sep 1, 09:00 → Completed Sep 2, 04:00 = 19h',
  },
  'quality.redoRate': {
    label: 'Redo rate',
    description:
      'Of the inspections completed in this period, the share sent back at least once. Read from the redo events the service emits rather than from the row, which is cleared on re-submission, so several redos on one inspection still count as one.',
    formula: 'Sent back at least once ÷ Completed',
  },
  'quality.withDamagesShare': {
    label: 'With damages',
    description:
      'Of the inspections completed in this period, the share carrying at least one active damage or a final damage record. The figure underneath averages active damages across EVERY completed inspection, not only the ones that have some.',
    formula: 'With at least one damage ÷ Completed',
  },
  'quality.crShare': {
    label: 'CR coverage',
    description:
      'Of the inspections completed in this period, the share whose estimation record carries a condition report.',
    formula: 'Carrying a condition report ÷ Completed',
  },
  'quality.estimateShare': {
    label: 'Estimate coverage',
    description:
      'Of the inspections completed in this period, the share whose estimation record carries a priced estimate.',
    formula: 'Carrying a priced estimate ÷ Completed',
  },
};

/**
 * The one thing every consumer of {@link IQuickStatBacklog} has to say out loud.
 *
 * `backlog`, `pending` and `total` are snapshots of the scoped inspection set as it stands — the
 * period and the row filters that shape every other number on the page do not touch them. A
 * surface that shows the queue beside period figures and does not say so invites the reader to
 * subtract one from the other.
 */
export const QUICK_STAT_BACKLOG_SCOPE_NOTE =
  'A live count of every inspection you can see. The period and filters above do not apply to it.';
